import React from "react";
import { Link as RouterLink } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";

export default function TermsOfService() {
    const theme = useTheme();

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.light}15 100%)`,
            }}
        >
            {/* Navigation Bar */}
            <Box
                sx={{
                    py: 2,
                    px: { xs: 2, sm: 4 },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography
                            variant="h5"
                            component={RouterLink}
                            to="/"
                            sx={{
                                fontWeight: 800,
                                color: theme.palette.primary.main,
                                textDecoration: "none",
                            }}
                        >
                            TapSheet
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/"
                            variant="contained"
                            color="primary"
                        >
                            Go to Home
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Content */}
            <Container maxWidth="md" sx={{ flex: 1, py: { xs: 4, md: 6 } }}>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 800,
                        mb: 4,
                        fontSize: { xs: "2rem", md: "2.5rem" },
                    }}
                >
                    Terms of Service
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 4 }}
                >
                    Last updated:{" "}
                    {new Date().toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    })}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            1. Acceptance of Terms
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            By accessing and using TapSheet ("the Service"), you
                            accept and agree to be bound by the terms and
                            provision of this agreement. If you do not agree to
                            these Terms of Service, please do not use the
                            Service.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            2. Description of Service
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            TapSheet is a personal finance and expense tracking
                            application available on iOS mobile devices and web
                            browsers. The Service allows users to:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Track income and expenses with categories
                                    and descriptions
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Manage monthly budgets and savings targets
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Use Apple Shortcuts for quick transaction
                                    entry from iOS devices
                                </Typography>
                            </li>
                        </Box>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            3. User Accounts
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            To use the Service, you must:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Provide accurate and complete information
                                    when creating an account
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Maintain the security of your account
                                    credentials
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Be responsible for all activities that occur
                                    under your account
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Notify us immediately of any unauthorized
                                    use of your account
                                </Typography>
                            </li>
                        </Box>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            4. Subscription and Payment
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            TapSheet offers paid subscription plans:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Subscriptions are available on a weekly or
                                    yearly basis
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Mobile app subscriptions are processed
                                    through Apple's App Store using RevenueCat
                                    and are subject to Apple's terms and
                                    conditions
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Subscriptions automatically renew unless
                                    cancelled at least 24 hours before the
                                    renewal date
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    You may manage and cancel your subscription
                                    through your Apple ID account settings or
                                    the App Store
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Refunds are handled in accordance with Apple
                                    App Store policies
                                </Typography>
                            </li>
                        </Box>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            5. User Content and Data
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            You retain ownership of all data and content you
                            provide to the Service. By using the Service, you
                            grant us a license to:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Store, process, and transmit your data to
                                    provide the Service
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Use anonymized data to improve our services
                                </Typography>
                            </li>
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                            You are responsible for ensuring you have the right
                            to upload any content you provide to the Service.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            6. Prohibited Uses
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            You agree not to use the Service:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    For any illegal purpose or in violation of
                                    any laws
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    To transmit any malicious code or viruses
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    To attempt to gain unauthorized access to
                                    the Service or related systems
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    To interfere with or disrupt the Service
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    To reverse engineer, decompile, or
                                    disassemble any part of the Service
                                </Typography>
                            </li>
                        </Box>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            7. Third-Party Integrations
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            The Service integrates with third-party services
                            including:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Google (for authentication via Google Sign
                                    In)
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Apple (for Sign in with Apple on iOS devices
                                    and Apple Shortcuts integration)
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    RevenueCat (for managing iOS in-app
                                    subscriptions)
                                </Typography>
                            </li>
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                            Your use of these integrations is subject to the
                            respective terms of service and privacy policies of
                            those third parties. We are not responsible for the
                            availability, accuracy, or practices of third-party
                            services.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            8. Disclaimer of Warranties
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE"
                            WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
                            IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED
                            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                            PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT
                            WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
                            SECURE, OR ERROR-FREE.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            9. Limitation of Liability
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT
                            BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                            CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF
                            PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR
                            INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR
                            OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF
                            THE SERVICE.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            10. Termination
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            We may terminate or suspend your account and access
                            to the Service immediately, without prior notice,
                            for any reason, including breach of these Terms.
                            Upon termination, your right to use the Service will
                            cease immediately. You may also terminate your
                            account at any time through your account settings.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            11. Changes to Terms
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            We reserve the right to modify these Terms of
                            Service at any time. We will notify users of any
                            material changes by posting the new Terms on this
                            page and updating the "Last updated" date. Your
                            continued use of the Service after such changes
                            constitutes acceptance of the new Terms.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            12. Contact Information
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            If you have any questions about these Terms of
                            Service, please contact us at{" "}
                            <Box
                                component="a"
                                href="mailto:snapsheetsocials@gmail.com"
                                sx={{
                                    color: theme.palette.primary.main,
                                    textDecoration: "none",
                                    fontWeight: 600,
                                    "&:hover": {
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                snapsheetsocials@gmail.com
                            </Box>
                        </Typography>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}
